// ─── Modelos de fila ─────────────────────────────────────────────────────────

export interface ExpenseRow {
  id: string;
  proveedor: string;
  fecha: string | null;
  concepto: string;
  categoria: string;
  tipo: string | null;
  total: number;
  base_imponible: number;
  iva_euros: number;
  estado: string;
  url: string | null;
  origen: string;
  importacion_id: string | null;
  factura_filename: string | null;
}

export interface ExpensePatch {
  proveedor?: string;
  fecha_factura?: string;
  concepto?: string | null;
  categoria?: string;
  tipo?: string | null;
  total?: number;
}

export interface IncomeRow {
  id: string;
  cliente: string;
  fecha: string | null;
  concepto: string;
  categoria: string;
  importe: number;
  estado: string;
  notas: string | null;
  origen: string;
  importacion_id: string | null;
}

export interface Importacion {
  id: string;
  tipo: "ingreso" | "gasto";
  fuente: string;
  nombre_archivo: string | null;
  fecha: string;
  filas_insertadas: number;
  filas_duplicadas: number;
  filas_error: number;
  notas: string | null;
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface DashboardData {
  period: { year: number; month: number; label: string };
  summary: {
    ingresos: number;
    gastos: number;
    balance: number;
    previous: { ingresos: number; gastos: number; balance: number };
    delta_pct: { ingresos: number | null; gastos: number | null; balance: number | null };
  };
  annual: {
    year: number;
    ingresos: number;
    gastos: number;
    balance: number;
  };
  ingresos_categorias: Array<{ name: string; amount: number; count: number }>;
  gastos_categorias: Array<{ name: string; amount: number; count: number }>;
  gastos_tipos: Array<{ name: string; amount: number; count: number }>;
  ingresos_recientes: IncomeRow[];
  gastos_recientes: ExpenseRow[];
  timeline: Array<{
    year: number;
    month: number;
    label: string;
    ingresos: number;
    gastos: number;
    balance: number;
  }>;
  generated_at: string;
}

// ─── Importación ─────────────────────────────────────────────────────────────

export interface ImportRowIngreso {
  cliente: string;
  fecha: string;
  concepto?: string;
  importe: number;
  categoria?: string;
  banco_concepto?: string;
  banco_fecha_operacion?: string;
  banco_origen?: string;
  banco_raw?: string;
}

export interface ImportRowGasto {
  proveedor: string;
  fecha_factura: string;
  concepto?: string;
  total: number;
  categoria?: string;
  banco_concepto?: string;
  banco_fecha_operacion?: string;
  banco_origen?: string;
  banco_raw?: string;
}

export interface ImportResult {
  importacion_id: string;
  inserted: number;
  skipped_duplicates: number;
  errors: Array<{ row: number; error: string }>;
}

export interface ApiError {
  error: string;
  detalle?: string;
}

// ─── Conciliación factura ↔ gasto (subida manual de PDF leído con IA) ────────

export interface FacturaExtraida {
  proveedor: string | null;
  fecha: string | null;
  importe: number | null;
  moneda: string | null;
  numero_factura: string | null;
  confianza: "alta" | "media" | "baja";
}

export interface FacturaMatchCandidate {
  gasto: ExpenseRow;
  importe_exacto: boolean;
  dias_diferencia: number | null;
  proveedor_similitud: number;
  confianza: "alta" | "media" | "baja";
  score: number;
}

export interface FacturaMatchResult {
  extracted: FacturaExtraida;
  candidates: FacturaMatchCandidate[];
}

// ─── Categorías (catálogo editable desde Configuración) ─────────────────────

export type CategoriaTipo = "gasto" | "ingreso";

export interface Categoria {
  id: string;
  nombre: string;
  tipo: CategoriaTipo;
  created_at: string;
}

// ─── Constantes ──────────────────────────────────────────────────────────────
//
// Estos arrays se usan como *fallback* mientras el contexto de categorías
// carga desde la API, o si la llamada falla. La fuente de verdad son los
// registros de la tabla `categorias` (gestionados en Configuración).

export const CATEGORIAS_GASTO = [
  "Alquiler",
  "Suministros",
  "Software/Licencias",
  "Seguros",
  "Material oficina",
  "Marketing",
  "Viajes",
  "Impuestos",
  "Salarios",
  "Renting",
] as const;
export type CategoriaGasto = (typeof CATEGORIAS_GASTO)[number];

export const CATEGORIAS_INGRESO = [
  "Honorarios",
  "Provisión de fondos",
  "Devolución",
  "Otros",
] as const;

export const TIPOS_GASTO = ["Fijo", "Variable"] as const;
export type TipoGasto = (typeof TIPOS_GASTO)[number];

// ─── Comisiones comercial (datos en Notion) ──────────────────────────────────

export type ComisionTipo = "Con ayuda" | "Sin ayuda" | "Personalizado";

/**
 * Convención de porcentaje: `pct` es una FRACCIÓN (0.035 = 3,5%), igual que lo
 * almacena Notion. El frontend muestra ×100 y convierte ÷100 al editar.
 */
export interface ComisionRow {
  id: string;
  nombre: string;
  num_exp: number | null;
  honorarios: number; // bruto tal cual en Notion
  iva_incluido: boolean;
  tipo: ComisionTipo | null;
  pct: number | null; // fracción
  primera_mitad: boolean;
  segunda_mitad: boolean;
  sin_comision: boolean;
  notas: string;
  neto: number; // base sin IVA (derivado)
  comision_total: number; // euros (derivado)
}

export interface ComisionPatch {
  tipo?: ComisionTipo | null;
  pct?: number | null; // fracción; sólo se respeta con tipo "Personalizado"
  iva_incluido?: boolean;
  primera_mitad?: boolean;
  segunda_mitad?: boolean;
  sin_comision?: boolean;
  notas?: string;
}

export interface ComisionesSyncResult {
  inserted: number;
  skipped_existing: number;
  remaining: number;
  last_num_exp: number | null;
}
